import React, { useState, useCallback, useMemo, useRef } from 'react';
import { ITableComp } from 'views/components/table/type';
import PushableSideBar from 'views/components/ui/sidebar';
import ConfigurationHeader from 'views/components/configurationHeader';
import TableComponent from 'views/components/table/Table';
import ConfigTableLayout from 'views/components/table/ConfigTableLayout';
import { useHistory } from 'react-router';
import { MED_APPOINTMENT_SUBTYPE_COLUMNS } from 'views/components/table/tableColumns/MEDAppointmentSubType';
import {
  IMEDAppointmentSubType,
  MEDAppointmentSubType
} from 'model/MEDAppointmentSubtype';
import { MED_APPT_SUBTYPE_TITLE } from 'utils/constants/configurations';
import { GET_MED_APPOINTMENT_SUBTYPES } from 'api/graphql/v2/queries/AppointmentTypes';
import { useQuery } from '@apollo/react-hooks';
import Content from 'views/components/ui/content';
import { TableType } from 'model/tableType';
import MEDAppointmentSubTypesForm from 'views/components/forms/configurations/MEDAppointmentSubType';
import { NEW_MED_APPOINTMENT_SUBTYPE_CONFIG } from 'utils/constants/init';
import { GET_APPT_TYPES_CONFIG } from 'api/graphql/v2/queries/AppointmentTypes';
import { scrollToTop } from 'utils/math';
import {
  ISortValue,
  MAPPER_FUNCTION,
  mapToTableState
} from 'utils/mappers/configuration';
import { IMEDAppointmentSubTypeConfig } from 'model/tableConfigurations';
import { EventTypes } from 'model/eventType';
import { StatusFilterTabs } from 'components/ui';

type StatusFilter = 'active' | 'inactive' | 'all';

const MEDAppointmentSubTypeConfig: React.FC = () => {
  const history = useHistory();
  const [currentForm, setCurrentForm] = useState(-1);
  const [sortValue, setSortValue] = useState<ISortValue>();
  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const formValues = useRef(NEW_MED_APPOINTMENT_SUBTYPE_CONFIG);

  const tableHeaders = MED_APPOINTMENT_SUBTYPE_COLUMNS;

  const isActiveVariable =
    statusFilter === 'all' ? undefined : statusFilter === 'active';

  const { loading, error, data } = useQuery(GET_MED_APPOINTMENT_SUBTYPES, {
    variables: { isActive: isActiveVariable },
    fetchPolicy: 'cache-and-network'
  });

  const { data: superTypedata } = useQuery(GET_APPT_TYPES_CONFIG, {
    variables: {
      eventType: EventTypes.MED
    },
    fetchPolicy: 'cache-and-network'
  });

  const addNew = useCallback(
    (data?: TableType) => {
      if (data) formValues.current = data as IMEDAppointmentSubType;
      else formValues.current = NEW_MED_APPOINTMENT_SUBTYPE_CONFIG;
      scrollToTop();
      setCurrentForm(formValues.current.id!);
    },
    [setCurrentForm]
  );

  const tableData = useMemo(() => {
    return data
      ? (mapToTableState(
          data.appointmentSubTypesConfig,
          addNew,
          MAPPER_FUNCTION.medAppointmentSubTypes,
          searchValue,
          sortValue
        ) as IMEDAppointmentSubTypeConfig[])
      : [];
  }, [data, addNew, searchValue, sortValue]);

  const onSearch = useCallback(
    value => {
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const onSort = useCallback(
    (comp: ITableComp, reverse: boolean) => {
      setSortValue({ comp, reverse });
    },
    [setSortValue]
  );

  const onFormClose = useCallback(() => {
    formValues.current = NEW_MED_APPOINTMENT_SUBTYPE_CONFIG;
    setCurrentForm(-1);
  }, [setCurrentForm]);

  const onGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handleStatusFilterChange = useCallback((value: StatusFilter) => {
    setStatusFilter(value);
  }, []);

  return (
    <>
      <PushableSideBar
        content={
          <MEDAppointmentSubTypesForm
            title={
              formValues.current.id
                ? `Edit ${MED_APPT_SUBTYPE_TITLE}`
                : `New ${MED_APPT_SUBTYPE_TITLE}`
            }
            superTypeList={superTypedata?.appointmentTypesConfig}
            initialValues={formValues.current}
            onFormClose={onFormClose}
            currentStatusFilter={isActiveVariable}
          />
        }
        visible={currentForm !== -1}
        pageComponent={
          <Content
            loading={loading}
            error={error}
            data={data}
            fullHeight={true}
          >
            {() => (
              <ConfigTableLayout>
                <ConfigurationHeader
                  addNew={addNew}
                  onSearch={onSearch}
                  title={MED_APPT_SUBTYPE_TITLE}
                  onClick={onGoBack}
                  visible={currentForm !== -1}
                />
                <StatusFilterTabs
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                />
                <TableComponent
                  headers={tableHeaders}
                  data={tableData}
                  sort={onSort}
                  primarySortedKey={MEDAppointmentSubType.superType}
                />
              </ConfigTableLayout>
            )}
          </Content>
        }
      />
    </>
  );
};

export default MEDAppointmentSubTypeConfig;
